import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  MenuItem
} from '@mui/material';
import { Save, Cancel, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import principalAPI from '../../services/principalAPI';
import { useAuth } from '../../context/AuthContext';
import { useFormValidation } from '@/hooks/useFormValidation';
import { required, phone, email } from '@/utils/validators';

const schema = {
  name: [required('Full name')],
  phone: [required('Primary phone number'), phone],
  father_phone: [phone],
  mother_phone: [phone],
  emergency_phone: [phone],
  parent_email: [email],
};

const AddStudent = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [classes, setClasses] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    admission_number: '',
    date_of_birth: '',
    class_id: '',
    father_name: '',
    father_phone: '',
    mother_name: '',
    mother_phone: '',
    emergency_phone: '',
    blood_group: '',
    parent_email: '',
    address: '',
    gender: ''
  });

  const { handleBlur, validateAll, applyServerErrors, showError } = useFormValidation(schema);

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const response = await principalAPI.getClasses();
        if (isMounted) {
          setClasses(response.data.classes || []);
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch student data when in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchStudent = async () => {
        try {
          setLoading(true);
          const response = await principalAPI.getStudent(id);
          const student = response.data.student;
          setFormData({
            name: student.name || '',
            phone: student.phone || '',
            admission_number: student.admission_number || '',
            date_of_birth: student.date_of_birth || '',
            class_id: student.class_id || '',
            father_name: student.father_name || '',
            father_phone: student.father_phone || '',
            mother_name: student.mother_name || '',
            mother_phone: student.mother_phone || '',
            emergency_phone: student.emergency_phone || '',
            blood_group: student.blood_group || '',
            parent_email: student.parent?.email || '',
            address: student.address || '',
            gender: student.gender || ''
          });
        } catch (error) {
          console.error('Error fetching student:', error);
          setError('Failed to load student data');
        } finally {
          setLoading(false);
        }
      };
      fetchStudent();
    }
  }, [isEditMode, id]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll(formData)) return;
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Clean up empty strings - convert to null for optional fields
      const cleanedData = {
        ...formData,
        class_id: formData.class_id || null,
        date_of_birth: formData.date_of_birth || null,
        father_name: formData.father_name || null,
        father_phone: formData.father_phone || null,
        mother_name: formData.mother_name || null,
        mother_phone: formData.mother_phone || null,
        emergency_phone: formData.emergency_phone || null,
        blood_group: formData.blood_group || null,
        parent_email: formData.parent_email || null,
        address: formData.address || null,
        gender: formData.gender || null
      };

      if (isEditMode) {
        await principalAPI.updateStudent(id, cleanedData);
      } else {
        await principalAPI.createStudent(cleanedData);
      }
      
      setSuccess(isEditMode ? 'Student updated successfully!' : 'Student created successfully!');
      
      if (isEditMode) {
        // For edit mode, navigate back after showing success message
        setTimeout(() => {
          navigate('/principal/students');
        }, 2000);
      } else {
        // Reset form for add mode
        setFormData({
          name: '',
          phone: '',
          admission_number: '',
          date_of_birth: '',
          class_id: '',
          father_name: '',
          father_phone: '',
          mother_name: '',
          mother_phone: '',
          emergency_phone: '',
          blood_group: '',
          parent_email: '',
          address: '',
          gender: ''
        });

        // Navigate back to students list after 2 seconds
        setTimeout(() => {
          navigate('/principal/students');
        }, 2000);
      }

    } catch (error) {
      const serverErrors = error.response?.data?.errors;
      if (serverErrors) {
        applyServerErrors(serverErrors);
      } else {
        setError(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} student`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          {isEditMode ? 'Edit Student' : 'Add New Student'}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/principal/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Student Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Student Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={(e) => handleBlur('name', e.target.value)}
                error={Boolean(showError('name'))}
                helperText={showError('name')}
                placeholder="Enter student's full name"
              />
            </Grid>

            <Grid item xs={12} md={6}>

              <TextField
                fullWidth
                required
                label="Primary Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={(e) => handleBlur('phone', e.target.value)}
                error={Boolean(showError('phone'))}
                helperText={showError('phone')}
                placeholder="+1234567890"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter student's address"
                multiline
                rows={2}
              />
            </Grid>

            {!isEditMode && (
              <Grid item xs={12} md={6}>
                <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
                  Admission Number will be auto-generated
                </Alert>
              </Grid>
            )}

            {isEditMode && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Admission Number"
                  name="admission_number"
                  value={formData.admission_number}
                  onChange={handleChange}
                  disabled
                  helperText="Admission number cannot be changed"
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Date of Birth"
                name="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                helperText="Optional: Student's gender"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Blood Group"
                name="blood_group"
                value={formData.blood_group}
                onChange={handleChange}
                helperText="Optional: Student's blood group"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="A+">A+</MenuItem>
                <MenuItem value="A-">A-</MenuItem>
                <MenuItem value="B+">B+</MenuItem>
                <MenuItem value="B-">B-</MenuItem>
                <MenuItem value="AB+">AB+</MenuItem>
                <MenuItem value="AB-">AB-</MenuItem>
                <MenuItem value="O+">O+</MenuItem>
                <MenuItem value="O-">O-</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Class"
                name="class_id"
                value={formData.class_id}
                onChange={handleChange}
                helperText="Optional: Assign student to a class"
              >
                <MenuItem value="">None</MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.name} - {cls.grade} {cls.section}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Parent/Guardian Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                Parent/Guardian Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Father Name"
                name="father_name"
                value={formData.father_name}
                onChange={handleChange}
                placeholder="Enter father's full name"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Father Phone Number"
                name="father_phone"
                value={formData.father_phone}
                onChange={handleChange}
                onBlur={(e) => handleBlur('father_phone', e.target.value)}
                error={Boolean(showError('father_phone'))}
                helperText={showError('father_phone')}
                placeholder="+1234567890"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mother Name"
                name="mother_name"
                value={formData.mother_name}
                onChange={handleChange}
                placeholder="Enter mother's full name"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mother Phone Number"
                name="mother_phone"
                value={formData.mother_phone}
                onChange={handleChange}
                onBlur={(e) => handleBlur('mother_phone', e.target.value)}
                error={Boolean(showError('mother_phone'))}
                helperText={showError('mother_phone')}
                placeholder="+1234567890"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Alternative Emergency Phone Number"
                name="emergency_phone"
                value={formData.emergency_phone}
                onChange={handleChange}
                onBlur={(e) => handleBlur('emergency_phone', e.target.value)}
                error={Boolean(showError('emergency_phone'))}
                placeholder="+1234567890"
                helperText={showError('emergency_phone') || 'Optional: Emergency contact'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Parent Email/Login (Optional)"
                name="parent_email"
                type="email"
                value={formData.parent_email}
                onChange={handleChange}
                onBlur={(e) => handleBlur('parent_email', e.target.value)}
                error={Boolean(showError('parent_email'))}
                placeholder="parent@example.com"
                helperText={showError('parent_email') || 'Optional: Create parent login account'}
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => navigate('/principal/students')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  disabled={loading}
                >
                  {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Student' : 'Create Student')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default AddStudent;
